import React, { useMemo, useState } from 'react';
import { apiFetch } from '../lib/api';
import { periodOptions, resolvePeriod } from '../lib/reportPeriods';

// @@CSV_REPORTS_v117@@ Period report from an uploaded Shopify "Export orders" CSV (no Shopify connection needed).
// @@CSV_ONLY_UNCONNECTED_v117_1@@ Offered (and accepted by the server) only for clients WITHOUT a connected store; ClientDetail hides this panel otherwise.
// Two steps on purpose: Preview (server dry-run: nothing saved, shows totals / exclusions / warnings / payout) then Generate.
// The CSV is read in the browser and posted once per step; the server parses it in memory and never stores it (it carries
// customer names / emails / addresses). Reports land on the client's per-client "manual-csv" pseudo-store, so the existing
// review list, PDF, credit-note and notify flow work unchanged.
const DASH = '—';
const MAX_BYTES = 25 * 1024 * 1024;

function money(v) {
  if (v == null || v === '') return DASH;
  const abs = Math.abs(Number(v)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return (Number(v) < 0 ? '-$' : '$') + abs;
}
const numOrUndef = (s) => (String(s).trim() === '' ? undefined : String(s).trim());

export default function CsvReportPanel({ clientId, clientName, existing, onGenerated }) {
  const [periodType, setPeriodType] = useState('month');
  const [periodStart, setPeriodStart] = useState('');
  const [ordersFile, setOrdersFile] = useState(null);
  const [noSales, setNoSales] = useState(false); // @@CSV_NO_SALES_v117_2@@ a period with no sales needs no file
  const [productsFile, setProductsFile] = useState(null);
  const [storeLabel, setStoreLabel] = useState('');
  const [sessions, setSessions] = useState('');
  const [conv, setConv] = useState('');
  const [analytics, setAnalytics] = useState('');
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [preview, setPreview] = useState(null);
  const [done, setDone] = useState('');
  const [fileKey, setFileKey] = useState(0);

  const sel = resolvePeriod(periodType, periodStart, existing);
  const options = useMemo(() => periodOptions(periodType, existing).options, [periodType, existing]);
  const invalidate = () => { setPreview(null); setDone(''); setErr(''); };

  async function send(dryRun) {
    setErr(''); setDone('');
    if (!noSales && !ordersFile) { setErr('Choose the orders CSV first, or tick “No sales this period”.'); return; }
    if (!noSales && ((ordersFile && ordersFile.size > MAX_BYTES) || (productsFile && productsFile.size > MAX_BYTES))) { setErr('A file is over 25 MB.'); return; }
    if (!dryRun && preview && preview.already_generated &&
        !window.confirm('This period was already generated. Replace it with this upload?\n\nIf it already has a credit note, the report line will warn when the amount changed.')) return;
    setBusy(dryRun ? 'preview' : 'save');
    try {
      const orders_csv = !noSales && ordersFile ? await ordersFile.text() : undefined;
      const products_csv = !noSales && productsFile ? await productsFile.text() : undefined;
      const body = JSON.stringify({
        orders_csv, products_csv, period_type: periodType, period_start: sel,
        store_label: numOrUndef(storeLabel), sessions: numOrUndef(sessions), conversion_rate_pct: numOrUndef(conv),
        analytics_net_sales: numOrUndef(analytics), dry_run: dryRun, no_sales: noSales ? true : undefined,
      });
      // text/plain on purpose: the server reads it with a route-scoped parser (the app-wide JSON limit is small).
      const res = await apiFetch(`/api/clients/${clientId}/shopify-csv-report`, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        setErr((res.status === 413 ? 'That file is too large to upload.' : (d.error || 'Could not read that file.')) +
          (d.code === 'no_rows' || d.code === 'no_orders_in_period' ? ' If this period really had no sales, tick “No sales this period” instead.' : ''));
        if (dryRun) setPreview(null);
        return;
      }
      if (dryRun) { setPreview(d.preview); return; }
      setPreview(null); setOrdersFile(null); setProductsFile(null); setNoSales(false); setFileKey((k) => k + 1);
      setDone('Report generated' + (d.lines_saved === false ? ' — but its line items could not be stored, so the PDF has no Line Items page. Generate it again to retry.' : '.'));
      if (onGenerated) await onGenerated(d.store_id, d.report.id);
    } catch (e) {
      setErr('Upload failed: ' + (e && e.message ? e.message : 'unknown error'));
    } finally { setBusy(''); }
  }

  const s = preview && preview.summary;
  const c = s && s.csv;
  const blocked = preview && (preview.designation_missing || preview.schema_ready === false);

  return (
    <section className="hq-panel hq-cd-card">
      <div className="hq-panel-head">
        <div>
          <h2>Report from a Shopify CSV</h2>
          <div className="hq-cd-sub">For clients without a Shopify store connection (clients with one generate their reports from Shopify as usual). In Shopify: Orders → Export → export by date → CSV for Excel (not “transaction histories”).</div>
        </div>
      </div>
      <div className="hq-cd-sub-block">
        <div className="hq-cd-row" style={{ padding: '0 0 10px', flexWrap: 'wrap', gap: 10 }}>
          <select className="hq-select" value={periodType} onChange={(e) => { setPeriodType(e.target.value); setPeriodStart(''); invalidate(); }}>
            <option value="month">Month</option>
            <option value="quarter">Quarter</option>
          </select>
          <select className="hq-select" value={sel} title="Period to report on" onChange={(e) => { setPeriodStart(e.target.value); invalidate(); }}>
            {options.map((o) => <option key={o.value} value={o.value}>{o.label}{o.inProgress ? ' — in progress' : ''}{o.done ? ' ✓' : ''}</option>)}
          </select>
          <label className="hq-cd-small">Orders CSV{' '}
            <input key={'o' + fileKey} type="file" accept=".csv,text/csv" disabled={noSales} onChange={(e) => { setOrdersFile(e.target.files[0] || null); invalidate(); }} />
          </label>
          <label className="hq-cd-check" title="The period had no orders (Shopify's export of an empty month is just a header row). Reports a zero month without a file.">
            <input type="checkbox" checked={noSales} onChange={(e) => { setNoSales(e.target.checked); invalidate(); }} /> <span>No sales this period</span>
          </label>
        </div>
        <div className="hq-cd-row" style={{ padding: '0 0 10px', flexWrap: 'wrap', gap: 10 }}>
          <label className="hq-cd-small" title="Optional. Shopify: Products → Export. Groups sizes/variants into their product exactly; without it, size words are stripped from item names.">Products CSV <em>optional</em>{' '}
            <input key={'p' + fileKey} type="file" accept=".csv,text/csv" disabled={noSales} onChange={(e) => { setProductsFile(e.target.files[0] || null); invalidate(); }} />
          </label>
        </div>
        <div className="hq-cd-row" style={{ padding: '0 0 10px', flexWrap: 'wrap', gap: 10 }}>
          <input className="hq-input" style={{ width: 200 }} placeholder={'Store name on report (' + (clientName || 'client') + ')'} value={storeLabel} onChange={(e) => { setStoreLabel(e.target.value); invalidate(); }} />
          <input className="hq-input" style={{ width: 120 }} inputMode="numeric" placeholder="Sessions" title="Optional — from Shopify Analytics (not in the orders export)" value={sessions} onChange={(e) => { setSessions(e.target.value); invalidate(); }} />
          <input className="hq-input" style={{ width: 120 }} inputMode="decimal" placeholder="Conversion %" title="Optional — e.g. 2.5" value={conv} onChange={(e) => { setConv(e.target.value); invalidate(); }} />
          <input className="hq-input" style={{ width: 190 }} inputMode="decimal" placeholder="Net sales per Analytics" title="Optional — the month's net sales from Shopify Analytics, to cross-check this CSV" value={analytics} onChange={(e) => { setAnalytics(e.target.value); invalidate(); }} />
        </div>
        <div className="hq-cd-row" style={{ padding: 0, gap: 10 }}>
          <button className="hq-btn hq-btn-ghost" disabled={!!busy || (!ordersFile && !noSales)} onClick={() => send(true)}>{busy === 'preview' ? 'Reading…' : 'Preview'}</button>
          <button className="hq-btn" disabled={!!busy || !preview || blocked} onClick={() => send(false)}>{busy === 'save' ? 'Generating…' : 'Generate report'}</button>
        </div>
        {err && <p className="hq-cd-warn" style={{ margin: '10px 0 0' }}>{err}</p>}
        {done && <p className="hq-cd-good" style={{ margin: '10px 0 0' }}>{done}</p>}

        {s && (
          <div style={{ marginTop: 14 }}>
            <h3 style={{ margin: '0 0 6px' }}>Preview — nothing saved yet</h3>
            <table className="hq-table" style={{ maxWidth: 520 }}>
              <tbody>
                <tr><td>Orders counted</td><td style={{ textAlign: 'right' }}>{s.headline.orders}</td></tr>
                <tr><td>Items sold</td><td style={{ textAlign: 'right' }}>{s.headline.net_items_sold}</td></tr>
                <tr><td><strong>Net sales</strong></td><td style={{ textAlign: 'right' }}><strong>{money(s.headline.net_sales)}</strong></td></tr>
                <tr><td>Taxes (not in net sales)</td><td style={{ textAlign: 'right' }}>{money(s.headline.taxes)}</td></tr>
                <tr><td>Shipping (not in net sales)</td><td style={{ textAlign: 'right' }}>{money(c.shipping_total)}</td></tr>
                <tr><td>Amount due to artist</td><td style={{ textAlign: 'right' }}>{money(s.payout && s.payout.artist_due)}</td></tr>
              </tbody>
            </table>
            {c.no_sales && <p className="hq-cd-small hq-cd-muted" style={{ margin: '8px 0' }}>No sales are reported for this period: a zero report, no file used. (No credit note can be made for $0.00.)</p>}
            {!c.no_sales && (<p className="hq-cd-small hq-cd-muted" style={{ margin: '8px 0' }}>
              File: {c.orders_in_file} order{c.orders_in_file === 1 ? '' : 's'}, {c.file_from} to {c.file_to}.
              {' '}Left out: {c.excluded.out_of_period} other-month, {c.excluded.cancelled} cancelled, {c.excluded.voided} voided
              {c.excluded.no_lines + c.excluded.bad_date > 0 ? ', ' + (c.excluded.no_lines + c.excluded.bad_date) + ' unreadable' : ''}.
              {' '}Item names grouped {c.grouping.used_products_file ? 'with the products file' : 'by removing size words'}.
              {c.lines_truncated ? ' Only the first 999 line items will print on the PDF; totals cover everything.' : ''}
            </p>)}
            {c.reconciliation && (
              <p className={c.reconciliation.matches ? 'hq-cd-good' : 'hq-cd-warn'} style={{ margin: '4px 0' }}>
                {c.reconciliation.matches
                  ? 'Matches Shopify Analytics (' + money(c.reconciliation.analytics_net_sales) + ').'
                  : 'CSV ' + money(c.reconciliation.csv_net_sales) + ' vs Analytics ' + money(c.reconciliation.analytics_net_sales) + ' — difference ' + money(c.reconciliation.difference) + '.'}
              </p>
            )}
            {(c.warnings || []).filter((w) => w.code !== 'reconciliation_mismatch').map((w, i) => (
              <p key={i} className="hq-cd-warn hq-cd-small" style={{ margin: '4px 0' }}>⚠ {w.message}{w.orders && w.orders.length ? ' (' + w.orders.slice(0, 8).join(', ') + (w.orders.length > 8 ? '…' : '') + ')' : ''}</p>
            ))}
            {preview.already_generated && <p className="hq-cd-warn hq-cd-small" style={{ margin: '4px 0' }}>This period was already generated — generating again replaces it.</p>}
            {preview.designation_missing && <p className="hq-cd-warn" style={{ margin: '4px 0' }}>Set a designation code for this client first (Profile tab).</p>}
            {preview.schema_ready === false && <p className="hq-cd-warn" style={{ margin: '4px 0' }}>Database update pending: apply 2026-11-02-report-lines.sql first.</p>}
            {!c.no_sales && (<div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 8 }}>
              <table className="hq-table" style={{ minWidth: 240 }}>
                <thead><tr><th>Top items</th><th style={{ textAlign: 'right' }}>Qty</th><th style={{ textAlign: 'right' }}>Net</th></tr></thead>
                <tbody>{s.top_items.slice(0, 5).map((it, i) => <tr key={i}><td>{it.product_title}</td><td style={{ textAlign: 'right' }}>{it.net_items_sold}</td><td style={{ textAlign: 'right' }}>{money(it.net_sales)}</td></tr>)}</tbody>
              </table>
              <table className="hq-table" style={{ minWidth: 200 }}>
                <thead><tr><th>Top states (billing)</th><th style={{ textAlign: 'right' }}>Net</th></tr></thead>
                <tbody>{s.top_states.map((st, i) => <tr key={i}><td>{st.region}</td><td style={{ textAlign: 'right' }}>{money(st.net_sales)}</td></tr>)}</tbody>
              </table>
            </div>)}
          </div>
        )}
      </div>
    </section>
  );
}
